<script setup lang="ts">
import {
  computed,
  type PropType,
  ref,
  watch
} from 'vue';
import {useI18n} from 'vue-i18n';
import {to12HourTimeString} from '@/utils/dateUtil.ts';
import {DialogType} from '@/constants/enums.ts';
import Svg from '@/components/Svg.vue';
import {baseStationSolid} from '@/assets/svgCanary.ts';

const emit = defineEmits(['selected-bedtime']);

const props = defineProps({
  defaultBedtime: {
    type: String,
    required: true
  },
  modelBedtime: {
    type: String as PropType<string | undefined>,
    required: true
  }
});

const showDialog = defineModel({
  type: Boolean,
  required: true
});

const {t} = useI18n();

const showTimePicker = ref<boolean>(false),
  showUploadDetails = ref<boolean>(false),
  selectedBedtime = ref<string | undefined>(undefined);

const displayBedtime = computed(() => selectedBedtime.value ? to12HourTimeString(selectedBedtime.value) : undefined);

const dataTransferHour = computed(() => {
  const hour = selectedBedtime.value ? selectedBedtime.value : props.defaultBedtime,
    hourPlus4 = parseInt(hour.split(':')[0]) + 4;

  return to12HourTimeString(hourPlus4);
});

function onModalBeforeShow() {
  selectedBedtime.value = props.modelBedtime ? props.modelBedtime : undefined;
}

function onModalHide() {
  showTimePicker.value = false;
  showUploadDetails.value = false;
  selectedBedtime.value = undefined;
}

watch(() => selectedBedtime.value, (nV, oV) => {
  if (nV !== oV && showDialog.value) {
    emit('selected-bedtime', nV);
  }
});
</script>

<template>
  <zbm-dialog v-model="showDialog"
              @before-show="onModalBeforeShow"
              @hide="onModalHide"
              :no-backdrop-dismiss="true"
              :no-esc-dismiss="true"
              :type="DialogType.w740p4030">

    <!-- Dialog Content -->
    <template #default>

      <!-- Header -->
      <div class="row q-dialog__header mb-40">
        <div class="col">

          <!-- Title -->
          <h3 class="text-center mb-30">{{ t('piq_edit_bedtime_title') }}</h3>

          <!-- Instructions -->
          <p>{{ t('hbs_bedtime_body') }}</p>
        </div>
      </div>

      <!-- Body -->
      <div class="row q-dialog__body mb-40">
        <div class="col">

          <!-- For example: -->
          <p class="text-weight-semibold font-size-14 mb-8">{{ t('hbs_for_example') }}</p>

          <!-- Steps -->
          <div class="column steps">

            <!-- Bed -->
            <div class="col-auto text-center">

              <!-- Icon -->
              <q-icon class="mb-8"
                      color="content-assets-hero-def"
                      name="fa-solid fa-bed"
                      size="40px" />

              <!-- Label -->
              <p class="text-weight-semibold font-size-12 text-all-small-caps mb-4">{{ t('piq_bedtime_selection') }}</p>

              <!-- Bedtime -->
              <p class="text-content-txt-secondary">
                {{ displayBedtime ? displayBedtime : props.defaultBedtime }}
              </p>
            </div>

            <!-- Arrow -->
            <div class="col column steps__arrow">

              <!-- Body -->
              <div class="col"><hr /></div>

              <!-- Head -->
              <div class="col-auto"><div class="head" /></div>
            </div>

            <!-- Clock -->
            <div class="col-auto text-center">

              <!-- Icon -->
              <q-icon class="mb-8"
                      color="content-assets-hero-def"
                      name="fa-solid fa-clock"
                      size="40px" />

              <!-- Label -->
              <p class="text-weight-semibold font-size-12 text-all-small-caps mb-4">{{ t('hbs_4_hours') }}</p>
            </div>

            <!-- Arrow -->
            <div class="col column steps__arrow">

              <!-- Body -->
              <div class="col"><hr /></div>

              <!-- Head -->
              <div class="col-auto"><div class="head" /></div>
            </div>

            <!-- Data -->
            <div class="col-auto text-center">

              <!-- Icon -->
              <Svg :view-box="baseStationSolid.viewBox"
                   :vector-paths="baseStationSolid.vectorPaths" />

              <!-- Label -->
              <p class="text-weight-semibold font-size-12 text-all-small-caps mb-4">{{ t('hbs_bedtime_transfer_label') }}</p>

              <!-- Transfer Hour -->
              <p class="text-content-txt-secondary">{{ dataTransferHour }}</p>
            </div>
          </div>

          <!-- My average bedtime is: -->
          <p class="font-size-20 text-content-txt-secondary text-center mb-16">{{ t('hbs_bedtime_input_label') }}</p>

          <!-- Bedtime Selection Display -->
          <div class="row justify-center mb-16">
            <div class="col-auto"
                 data-testid="averageBedtime">

              <!-- Display -->
              <zbm-input-with-validation v-model="displayBedtime"
                                         @on-click="showTimePicker = true"
                                         class="cursor-pointer"
                                         for="bedtimeSelection"
                                         :input-width="$zb.enums.FormElWidthEnum.qFormElFull"
                                         name="bedtimeSelection"
                                         :placeholder="props.defaultBedtime"
                                         :readonly="true" />

              <!-- Bedtime Selection -->
              <q-popup-proxy v-model="showTimePicker"
                             anchor="center middle"
                             class="bg-transparent overflow-visible"
                             no-parent-event
                             self="center middle"
                             style="min-width: auto;"
                             transition-hide="scale"
                             transition-show="scale">
                <q-time v-model="selectedBedtime"
                        color="primitives-values-100"
                        :minute-options="[0]">
                  <template #default>
                    <zbm-btn v-close-popup
                             :color="$zb.enums.ButtonStdColorEnum.accent"
                             data-testid="continueButton"
                             :height="$zb.enums.ButtonHeightEnum.sm"
                             :label="t('acc_gen_continue')"
                             :width="$zb.enums.ButtonWidthEnum.wFull" />
                  </template>
                </q-time>
              </q-popup-proxy>
            </div>
          </div>

          <!-- Tap number to edit -->
          <p class="call-to-action">{{ t('survey_range_tap') }}</p>

          <!-- Selection Message -->
          <div class="selection-msg">
            <p class="mb-20">{{ t('hbs_summary_intro_edit', [displayBedtime]) }}</p>
            <zbm-btn-link @click="showUploadDetails = true"
                          :class="['font-size-14 text-underline', { 'mb-20' : showUploadDetails }]"
                          data-testid="bedtimeInfo"
                          :label="t('piq_bedtime_selection_question')"
                          text-color="content-txt-footer-alert" />
            <p v-if="showUploadDetails">{{ t('piq_bedtime_selection_upload_details') }}</p>
          </div>
        </div>
      </div>

      <!-- Footer (CTA button(s)) -->
      <div class="row q-dialog__footer">

        <!-- Save Button -->
        <div class="col">
          <zbm-btn v-close-popup
                   :color="$zb.enums.ButtonStdColorEnum.accent"
                   data-testid="saveButton"
                   :disable="selectedBedtime"
                   :height="$zb.enums.ButtonHeightEnum.lg"
                   :label="t('gen_save')"
                   :width="$zb.enums.ButtonWidthEnum.wFull" />
        </div>
      </div>
    </template>
  </zbm-dialog>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'sass:math';

// Body
.q-dialog__body {

  // Steps
  .steps { // .column/.row
    $steps-gutter: 8px;
    $steps-padding: 16px;

    padding-block: $steps-padding - math.div($steps-gutter, 2);
    padding-inline: $steps-padding;
    border: $generic-border;
    margin-block-end: 32px;

    > [class*="col"] {
      padding-block: math.div($steps-gutter, 2);
    }

    // Data -> Icon
    svg {
      width: 41px;
      height: auto;
      color: map.get($co-brand-clrs, content-trg-secondary-def);
    }

    // Arrow
    &__arrow { // [class*="col"].column/.row
      color: map.get($co-brand-clrs, content-assets-dta-viz-bg-mkr);
      flex-wrap: nowrap;
      align-items: center;

      hr {
        height: 20px;
        border-style: solid;
        border-color: currentColor;
        margin: 0;
      }

      .head { // <div>
        border-block-start: 10px solid currentColor;
        border-inline: 6px solid transparent;
      }
    }

    @media (min-width: 526px) {
      flex-direction: row;
      padding-block: $steps-padding;
      padding-inline: $steps-padding - math.div($steps-gutter, 2);

      > [class*="col"] {
        padding-block: 0;
        padding-inline: math.div($steps-gutter, 2);
      }

      > .col-auto { // [class*="col"]
        $col-auto-w: 120px + $steps-gutter;

        width: $col-auto-w;
        max-width: $col-auto-w;
        flex-basis: $col-auto-w;
      }

      // Arrow
      &__arrow { // [class*="col"].column/.row
        height: 40px;
        color: map.get($co-brand-clrs, content-assets-dta-viz-bg-mkr);
        flex-direction: row;

        hr {
          height: initial;
        }

        .head { // <div>
          border-block: 6px solid transparent;
          border-inline-start: 10px solid currentColor;
          border-inline-end: none;
        }
      }
    }
  }

  // Bedtime Selection Display
  :deep(.q-input.cursor-pointer) { // .q-field

    // Native
    .q-field__native { // <input>
      cursor: pointer;
      max-width: 320px;
      font-size: 72px;
      line-height: 95px;
      color: $primitives-pop-100;
      text-align: center;
      padding-block: 0;
      border-block-end: 3px solid $cnt-def-page-divider;
    }
  }

  // Tap number to edit / Edit Bedtime
  .call-to-action { // <p>
    font-style: italic;
    font-weight: map.get($text-weights, bold);
    font-size: map.get($font-sizes, '20');
    line-height: map.get($line-heights, '24');
    color: map.get($co-brand-clrs, content-txt-dta-viz-lbl);
    text-align: center;
    margin-block-end: 40px;
  }

  // Selection Message
  .selection-msg { // <div>
    background-color: map.get($co-brand-clrs, cnt-widget-alert-help);
    font-size: map.get($font-sizes, '14');
    color: map.get($co-brand-clrs, content-txt-footer-alert);
    padding-block: 10px;
    padding-inline: 20px;
    border-radius: 6px;
  }
}
</style>
